import { OutputItem } from '../types';
import { getAvailablePages } from './PageSystem';
import SoundManager from './SoundManager';
import { ALL_ACHIEVEMENTS, getUnlocked, getVisitedPages, resetAll } from './Achievements';
import { recordCommand } from './SessionTracker';
import {
  fetchWeatherFor,
  fetchAllCities,
  getCachedWeatherFor,
  describeCode,
  CITIES,
  CITY_KEYS,
  CityKey,
} from './KarakotoWeather';
import { getCachedGeo } from './Tracker';
import { buildGreeting } from './LocaleMessage';

const PAGE_COMMANDS: Record<string, { pageId: string; message: string }> = {
  'ABOUT': { pageId: 'about', message: 'OPENING ABOUT PAGE...' },
  'SKILLS': { pageId: 'skills', message: 'OPENING SKILLS PAGE...' },
  'CONTACT': { pageId: 'contact', message: 'OPENING CONTACT PAGE...' },
  'MESSAGE': { pageId: 'message', message: 'OPENING MESSAGE FORM...' },
  // Archived: WEBSITES page removed 2026-07-11 (see archives/page-websites-2026-07-11.md)
  'SECRET': { pageId: 'secret', message: 'ACCESSING SECRET PAGE...' },
  'GALAXY': { pageId: 'galaxy', message: 'LOADING STELLAR CARTOGRAPHY...' },
  'BOOKS': { pageId: 'books', message: 'OPENING BOOKS PAGE...' },

  'CONSCIOUSNESS': { pageId: 'consciousness', message: 'INITIALIZING CONSCIOUSNESS EXPERIMENT...' },
  'DISCONNECT': { pageId: 'disconnect', message: 'LOADING SYNTHETIC MEMORY STREAM...' },
  'DUNGEON': { pageId: 'dungeon', message: 'ENTERING THE DUNGEON OF SELF...' },
  'REALITY': { pageId: 'reality', message: 'ACCESSING NARRATIVE ARCHITECTURE...' },
  'MEMORY': { pageId: 'memory', message: 'INITIALIZING CHRONOTUNNEL SEQUENCE...' },
  'CHRONOTUNNEL': { pageId: 'chronotunnel', message: 'INITIALIZING CHRONOTUNNEL SEQUENCE...' },
  'MENGER': { pageId: 'menger', message: 'ENTERING THE MENGER SPONGE...' },
  'SPONGE': { pageId: 'menger', message: 'ENTERING THE MENGER SPONGE...' },
  'FRACTAL': { pageId: 'menger', message: 'ENTERING THE MENGER SPONGE...' },
  'SIMULATION': { pageId: 'simulation', message: 'ACCESSING LIVING SIMULATION ENGINE...' },
  'ALYCE': { pageId: 'alyce', message: 'ENTERING DIGITAL RABBIT HOLE...' },
  'RABBIT': { pageId: 'rabbit', message: 'INITIALIZING WHITE RABBIT PROTOCOL...' },
  'WHITE RABBIT': { pageId: 'rabbit', message: 'INITIALIZING WHITE RABBIT PROTOCOL...' },
  'FOLLOW THE RABBIT': { pageId: 'rabbit', message: 'INITIALIZING WHITE RABBIT PROTOCOL...' },
  'REMEMBER': { pageId: 'remember', message: 'ACCESSING THE ARCHIVE OF THE UNWRITTEN...' },
  'T': { pageId: 'agrou', message: 'AGROU AGROU GROU INITIALIZING...' },
  'SUMMIT': { pageId: 'summit', message: 'LOADING SUMMIT VISUALIZATION...' },
  'MOUNTAINS': { pageId: 'summit', message: 'LOADING SUMMIT VISUALIZATION...' },
  'PEAKS': { pageId: 'summit', message: 'LOADING SUMMIT VISUALIZATION...' },

  'EMERGENCE': { pageId: 'emergence', message: 'INITIALIZING EMERGENCE SIMULATION...' },
  'DRIFT': { pageId: 'drift', message: 'LOADING SPACE STATION MEMORY STREAM...' },
  'TYPEWRITER': { pageId: 'typewriter', message: '零式タイプライター 起動中...' },
  'HYAKKI': { pageId: 'hyakki', message: '百鬼夜行 — SUMMONING THE NIGHT PARADE...' },
  'BASIC': { pageId: 'basic', message: 'LOADING BASIC V2...' },
  'PUPPET': { pageId: 'puppet', message: 'INBOUND CONNECTION DETECTED...' },
  'PUPPET MASTER': { pageId: 'puppet', message: 'INBOUND CONNECTION DETECTED...' },
  'TACHIKOMA': { pageId: 'tachikoma', message: 'BOOTING TACHIKOMA THINK TANK...' },
  'DIVE': { pageId: 'dive', message: 'DIVING INTO THE NET...' },
  'ALIEN': { pageId: 'alien', message: 'BOOTING MU/TH/UR 6000 MAINFRAME...' },
  'MOTHER': { pageId: 'alien', message: 'BOOTING MU/TH/UR 6000 MAINFRAME...' },
  'MUTHUR': { pageId: 'alien', message: 'BOOTING MU/TH/UR 6000 MAINFRAME...' },
  'MU/TH/UR': { pageId: 'alien', message: 'BOOTING MU/TH/UR 6000 MAINFRAME...' },
  'NOSTROMO': { pageId: 'alien', message: 'BOOTING MU/TH/UR 6000 MAINFRAME...' },
  '937': { pageId: 'alien', message: 'ACCESSING SPECIAL ORDER 937...' },
  'FALLOUT': { pageId: 'fallout', message: 'ROBCO INDUSTRIES (TM) TERMLINK PROTOCOL...' },
  'ROBCO': { pageId: 'fallout', message: 'ROBCO INDUSTRIES (TM) TERMLINK PROTOCOL...' },
  'TERMLINK': { pageId: 'fallout', message: 'ROBCO INDUSTRIES (TM) TERMLINK PROTOCOL...' },
  'VAULT': { pageId: 'fallout', message: 'ACCESSING VAULT 4000 OVERSEER SUBSYSTEM...' },
  'VAULT 4000': { pageId: 'fallout', message: 'ACCESSING VAULT 4000 OVERSEER SUBSYSTEM...' },
  'PIPBOY': { pageId: 'fallout', message: 'ROBCO INDUSTRIES (TM) TERMLINK PROTOCOL...' },
  'KARAKOTO': { pageId: 'karakoto', message: '唐琴へようこそ...' },
  'ROTARY': { pageId: 'rotary', message: 'LOADING ROTARY BEIRUT CEDARS...' },
  '3CAUSES': { pageId: '3causes', message: 'LOADING 3CAUSES NGO INFO...' },
  'GITHUB': { pageId: 'github', message: 'OPENING GITHUB PROJECTS...' },
  'DISASTERFIELDGUIDE': { pageId: 'disasterfieldguide', message: 'OPENING DISASTER FIELD GUIDE...' },
  'DFG': { pageId: 'disasterfieldguide', message: 'OPENING DISASTER FIELD GUIDE...' },
  'FIELDGUIDE': { pageId: 'disasterfieldguide', message: 'OPENING DISASTER FIELD GUIDE...' },
  // Archived: EMERGENCE2, DUNGEON1, DUNGEON2 (visualizations kept in src/components/ but no longer exposed)
  'MYDATA': { pageId: 'mydata', message: 'LOADING PERSONAL DATA REGISTRY...' },
  'MY DATA': { pageId: 'mydata', message: 'LOADING PERSONAL DATA REGISTRY...' },
  'MATRIX': { pageId: 'legacy', message: 'ENTERING THE MATRIX...' },
};

function findSimilarCommand(input: string): string | null {
  const allCommands = ['HELP', 'DIR', 'LS', 'CLEAR', 'CLS', 'EXIT', 'QUIT', ...Object.keys(PAGE_COMMANDS)];
  const inputLower = input.toLowerCase();

  for (const cmd of allCommands) {
    const cmdLower = cmd.toLowerCase();
    // Check if input is close enough (within 2 character edit distance for short commands, or starts similarly)
    if (cmdLower.length > 2 && inputLower.length > 2) {
      // Simple similarity: same start or same end
      if (cmdLower.startsWith(inputLower.substring(0, 3)) || inputLower.startsWith(cmdLower.substring(0, 3))) {
        return cmd;
      }
    }
  }
  return null;
}

export const executeCommand = (
  input: string, 
  currentOutput: OutputItem[], 
  onOpenPage?: (pageId: string) => void
): OutputItem[] => {
  const command = input.trim().toUpperCase();

  // Log the command into the session trace (skip empty input)
  if (command) recordCommand(command);

  // Add the command to the output
  const newOutput: OutputItem[] = [
    ...currentOutput,
    { type: 'command', content: command, key: `cmd-${Date.now()}` }
  ];
  
  // Process the command
  if (command === '') {
    // Empty command, just return
    return newOutput;
  } else if (command === 'HELP') {
    return processHelpCommand(newOutput, onOpenPage);
  } else if (command === 'HELP SECRET') {
    return processHelpSecretCommand(newOutput, onOpenPage);
  } else if (command === 'DIR' || command === 'LS') {
    return processListCommand(newOutput, onOpenPage);
  } else if (command === 'CLEAR' || command === 'CLS') {
    return processClearCommand();
  } else if (command === 'ACHIEVEMENTS' || command === 'TROPHIES') {
    return processAchievementsCommand(newOutput);
  } else if (command === 'PROGRESS' || command === 'STATS') {
    return processProgressCommand(newOutput);
  } else if (command === 'RESET PROGRESS') {
    resetAll();
    return [
      ...newOutput,
      { type: 'text', content: 'PROGRESS WIPED. ALL ACHIEVEMENTS LOCKED. WELCOME BACK TO ZERO.', key: `reset-${Date.now()}` }
    ];
  } else if (command === 'FORTUNE') {
    return processFortuneCommand(newOutput);
  } else if (command === 'KOAN') {
    return processKoanCommand(newOutput);
  } else if (command === 'MOON') {
    return processMoonCommand(newOutput);
  } else if (command === 'TEA') {
    return processTeaCommand(newOutput);
  } else if (command === 'SUDO' || command.startsWith('SUDO ')) {
    return processSudoCommand(newOutput, command);
  } else if (command === 'WHOIS' || command.startsWith('WHOIS ')) {
    return processWhoisCommand(newOutput, command);
  } else if (command === 'WEATHER') {
    return processWeatherCommand(newOutput, null);
  } else if (command === 'WEATHER KARAKOTO' || command === 'KARAKOTO WEATHER') {
    return processWeatherCommand(newOutput, 'karakoto');
  } else if (command === 'WEATHER LYON' || command === 'LYON WEATHER') {
    return processWeatherCommand(newOutput, 'lyon');
  } else if (command === 'WEATHER BEIRUT' || command === 'BEIRUT WEATHER') {
    return processWeatherCommand(newOutput, 'beirut');
  } else if (command === 'WEATHER KAMPALA' || command === 'KAMPALA WEATHER') {
    return processWeatherCommand(newOutput, 'kampala');
  } else if (command === 'WHEREAMI' || command === 'WHERE AM I') {
    return processWhereamiCommand(newOutput);
  } else if (PAGE_COMMANDS[command]) {
    const { pageId, message } = PAGE_COMMANDS[command];
    if (onOpenPage) onOpenPage(pageId);
    return [
      ...newOutput,
      { type: 'text', content: message, key: `open-${Date.now()}` }
    ];
  } else if (command.startsWith('OPEN ')) {
    const pageName = command.substring(5).toLowerCase();
    const pages = getAvailablePages();
    const page = pages.find(p => p.id === pageName);

    // Check available pages first
    if (page && onOpenPage) {
      onOpenPage(page.id);
      return [
        ...newOutput,
        { type: 'text', content: `OPENING ${page.name.toUpperCase()} PAGE...`, key: `open-${Date.now()}` }
      ];
    }

    // Check PAGE_COMMANDS map by uppercase pageName
    const upperPageName = pageName.toUpperCase();
    if (PAGE_COMMANDS[upperPageName] && onOpenPage) {
      const { pageId, message } = PAGE_COMMANDS[upperPageName];
      onOpenPage(pageId);
      return [
        ...newOutput,
        { type: 'text', content: message, key: `open-${Date.now()}` }
      ];
    }

    SoundManager.playSound('error');
    return [
      ...newOutput,
      { type: 'error', content: `PAGE NOT FOUND: ${pageName}`, key: `err-${Date.now()}` }
    ];
  } else if (command === 'EXIT' || command === 'QUIT') {
    // This would be handled by the parent component
    return [
      ...newOutput,
      { type: 'text', content: 'USE THE X BUTTON OR ESC KEY TO CLOSE THE TERMINAL', key: `exit-${Date.now()}` }
    ];
  } else {
    SoundManager.playSound('error');
    const suggestion = findSimilarCommand(command);
    const items: OutputItem[] = [
      ...newOutput,
      { type: 'error', content: `UNKNOWN COMMAND: ${command}`, key: `err-${Date.now()}` },
    ];
    if (suggestion) {
      items.push({ type: 'text', content: `DID YOU MEAN "${suggestion}"?`, key: `suggest-${Date.now()}` });
    }
    items.push({ type: 'text', content: 'TYPE "HELP" FOR AVAILABLE COMMANDS OR "DIR" TO SEE PAGES', key: `help-${Date.now()}` });
    return items;
  }
};

const processHelpCommand = (output: OutputItem[], onOpenPage?: (pageId: string) => void): OutputItem[] => {
  const timestamp = Date.now();

  return [
    ...output,
    { type: 'text', content: '=== STANDARD COMMANDS ===', key: `help-std-title-${timestamp}` },
    { type: 'text', content: 'HELP / DIR / LS / CLEAR / ACHIEVEMENTS / PROGRESS / ', key: `help-1-${timestamp}`, inline: true },
    { type: 'link', content: 'ABOUT', key: `help-2-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('about') : undefined },
    { type: 'text', content: ' / ', key: `help-3-${timestamp}`, inline: true },
    { type: 'link', content: 'SKILLS', key: `help-4-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('skills') : undefined },
    { type: 'text', content: ' / ', key: `help-5-${timestamp}`, inline: true },
    { type: 'link', content: 'CONTACT', key: `help-6-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('contact') : undefined },
    { type: 'text', content: ' / ', key: `help-6b-${timestamp}`, inline: true },
    { type: 'link', content: 'MESSAGE', key: `help-msg-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('message') : undefined },
    { type: 'text', content: ' / ', key: `help-9-${timestamp}`, inline: true },
    { type: 'link', content: 'GALAXY', key: `help-10-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('galaxy') : undefined },
    { type: 'text', content: ' / ', key: `help-11-${timestamp}`, inline: true },
    { type: 'link', content: 'BOOKS', key: `help-12-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('books') : undefined },
    { type: 'text', content: ' / ', key: `help-12b-${timestamp}`, inline: true },
    { type: 'link', content: 'KARAKOTO', key: `help-12c-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('karakoto') : undefined },
    { type: 'text', content: ' / ', key: `help-12d-${timestamp}`, inline: true },
    { type: 'link', content: 'ROTARY', key: `help-12e-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('rotary') : undefined },
    { type: 'text', content: ' / ', key: `help-12f-${timestamp}`, inline: true },
    { type: 'link', content: '3CAUSES', key: `help-12g-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('3causes') : undefined },
    { type: 'text', content: ' / ', key: `help-12h-${timestamp}`, inline: true },
    { type: 'link', content: 'GITHUB', key: `help-12i-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('github') : undefined },
    { type: 'text', content: ' / ', key: `help-12j-${timestamp}`, inline: true },
    { type: 'link', content: 'DISASTERFIELDGUIDE', key: `help-12k-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('disasterfieldguide') : undefined },
    { type: 'text', content: '', key: `help-break-${timestamp}` },
    { type: 'text', content: '=== EXPERIMENTAL ZONE ===', key: `help-exp-title-${timestamp}` },
    { type: 'link', content: 'CONSCIOUSNESS', key: `help-16-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('consciousness') : undefined },
    { type: 'text', content: ' / ', key: `help-16b-${timestamp}`, inline: true },
    { type: 'link', content: 'DISCONNECT', key: `help-18-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('disconnect') : undefined },
    { type: 'text', content: ' / ', key: `help-18b-${timestamp}`, inline: true },
    { type: 'link', content: 'DRIFT', key: `help-drift-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('drift') : undefined },
    { type: 'text', content: ' / ', key: `help-19-${timestamp}`, inline: true },
    { type: 'link', content: 'DUNGEON', key: `help-20-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('dungeon') : undefined },
    { type: 'text', content: ' / ', key: `help-21-${timestamp}`, inline: true },
    { type: 'link', content: 'SIMULATION', key: `help-22-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('simulation') : undefined },
    { type: 'text', content: ' / ', key: `help-28-${timestamp}`, inline: true },
    { type: 'link', content: 'ALYCE', key: `help-29-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('alyce') : undefined },
    { type: 'text', content: ' / ', key: `help-30-${timestamp}`, inline: true },
    { type: 'link', content: 'RABBIT', key: `help-31-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('rabbit') : undefined },
    { type: 'text', content: ' / ', key: `help-32-${timestamp}`, inline: true },
    { type: 'link', content: 'REMEMBER', key: `help-33-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('remember') : undefined },
    { type: 'text', content: ' / ', key: `help-34-${timestamp}`, inline: true },
    { type: 'link', content: 'SUMMIT', key: `help-39-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('summit') : undefined },
    { type: 'text', content: ' / ', key: `help-40-${timestamp}`, inline: true },
    { type: 'link', content: 'EMERGENCE', key: `help-43-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('emergence') : undefined },
    { type: 'text', content: ' / ', key: `help-44-${timestamp}`, inline: true },
    { type: 'link', content: 'MATRIX', key: `help-47-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('legacy') : undefined },
    { type: 'text', content: ' / ', key: `help-47b-${timestamp}`, inline: true },
    { type: 'link', content: 'TYPEWRITER', key: `help-tw-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('typewriter') : undefined },
    { type: 'text', content: ' / ', key: `help-tw2-${timestamp}`, inline: true },
    { type: 'link', content: 'HYAKKI', key: `help-hk-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('hyakki') : undefined },
    { type: 'text', content: ' / ', key: `help-hk2-${timestamp}`, inline: true },
    { type: 'link', content: 'BASIC', key: `help-basic-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('basic') : undefined },
    { type: 'text', content: ' / ', key: `help-pm-sep1-${timestamp}`, inline: true },
    { type: 'link', content: 'PUPPET', key: `help-pm-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('puppet') : undefined },
    { type: 'text', content: ' / ', key: `help-tk-sep-${timestamp}`, inline: true },
    { type: 'link', content: 'TACHIKOMA', key: `help-tk-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('tachikoma') : undefined },
    { type: 'text', content: ' / ', key: `help-dv-sep-${timestamp}`, inline: true },
    { type: 'link', content: 'DIVE', key: `help-dv-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('dive') : undefined },
    { type: 'text', content: ' / ', key: `help-al-sep-${timestamp}`, inline: true },
    { type: 'link', content: 'ALIEN', key: `help-al-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('alien') : undefined },
    { type: 'text', content: ' / ', key: `help-ct-sep-${timestamp}`, inline: true },
    { type: 'link', content: 'CHRONOTUNNEL', key: `help-ct-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('chronotunnel') : undefined },
    { type: 'text', content: ' / ', key: `help-mg-sep-${timestamp}`, inline: true },
    { type: 'link', content: 'MENGER', key: `help-mg-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('menger') : undefined },
    { type: 'text', content: ' / ', key: `help-fo-sep-${timestamp}`, inline: true },
    { type: 'link', content: 'FALLOUT', key: `help-fo-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('fallout') : undefined },
    { type: 'text', content: '', key: `help-break2-${timestamp}` },
    { type: 'text', content: 'OTHER: OPEN [PAGE] / EXIT / QUIT', key: `help-48-${timestamp}` }
  ];
};

const processHelpSecretCommand = (output: OutputItem[], onOpenPage?: (pageId: string) => void): OutputItem[] => {
  const timestamp = Date.now();

  return [
    ...output,
    { type: 'text', content: 'HELP / DIR / LS / CLEAR / ', key: `help-secret-1-${timestamp}`, inline: true },
    { type: 'link', content: 'ABOUT', key: `help-secret-2-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('about') : undefined },
    { type: 'text', content: ' / ', key: `help-secret-3-${timestamp}`, inline: true },
    { type: 'link', content: 'SKILLS', key: `help-secret-4-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('skills') : undefined },
    { type: 'text', content: ' / ', key: `help-secret-5-${timestamp}`, inline: true },
    { type: 'link', content: 'CONTACT', key: `help-secret-6-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('contact') : undefined },
    { type: 'text', content: ' / ', key: `help-secret-9-${timestamp}`, inline: true },
    { type: 'link', content: 'GALAXY', key: `help-secret-10-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('galaxy') : undefined },
    { type: 'text', content: ' / ', key: `help-secret-11-${timestamp}`, inline: true },
    { type: 'link', content: 'BOOKS', key: `help-secret-12-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('books') : undefined },
    { type: 'text', content: ' / ', key: `help-secret-13-${timestamp}`, inline: true },
    { type: 'link', content: 'CONSCIOUSNESS', key: `help-secret-16-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('consciousness') : undefined },
    { type: 'text', content: ' / ', key: `help-secret-17-${timestamp}`, inline: true },
    { type: 'link', content: 'DISCONNECT', key: `help-secret-18-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('disconnect') : undefined },
    { type: 'text', content: ' / ', key: `help-secret-19-${timestamp}`, inline: true },
    { type: 'link', content: 'DUNGEON', key: `help-secret-20-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('dungeon') : undefined },
    { type: 'text', content: ' / ', key: `help-secret-30-${timestamp}`, inline: true },
    { type: 'link', content: 'ALYCE', key: `help-secret-31-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('alyce') : undefined },
    { type: 'text', content: ' / ', key: `help-secret-25-${timestamp}`, inline: true },
    { type: 'link', content: 'RABBIT', key: `help-secret-26-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('rabbit') : undefined },
    { type: 'text', content: ' / ', key: `help-secret-27-${timestamp}`, inline: true },
    { type: 'link', content: 'REMEMBER', key: `help-secret-28-${timestamp}`, inline: true, onClick: onOpenPage ? () => onOpenPage('remember') : undefined },
    { type: 'text', content: ' / OPEN [PAGE] / EXIT / QUIT', key: `help-secret-31-${timestamp}`, inline: true }
  ];
};

const processListCommand = (
  output: OutputItem[], 
  onOpenPage?: (pageId: string) => void
): OutputItem[] => {
  const pages = getAvailablePages();
  const timestamp = Date.now();
  
  // Categorize pages
  const standardPages = pages.filter(page =>
    ['about', 'skills', 'contact', 'message', 'galaxy', 'books', 'summit', 'karakoto', 'rotary', '3causes', 'github', 'disasterfieldguide'].includes(page.id)
  );
  
  const result: OutputItem[] = [
    ...output,
    { type: 'text', content: '=== AVAILABLE PAGES ===', key: `dir-std-title-${timestamp}` },
  ];
  
  // Add standard pages
  standardPages.forEach((page, index) => {
    result.push({
      type: 'link',
      content: page.name.toUpperCase() + '.TXT',
      key: `dir-std-${index}-${timestamp}`,
      onClick: onOpenPage ? () => onOpenPage(page.id) : undefined
    });
  });
  
  return result;
};

const processClearCommand = (): OutputItem[] => {
  const lines: OutputItem[] = [
    { type: 'clear', content: '', key: `clear-${Date.now()}` },
    { type: 'text', content: '*** TERMINAL 64 BASIC V2 ***', key: `welcome-1-${Date.now()}` },
    { type: 'text', content: 'READY.', key: `welcome-3-${Date.now()}` },
  ];
  return lines;
};

const processAchievementsCommand = (output: OutputItem[]): OutputItem[] => {
  const t = Date.now();
  const unlocked = new Set(getUnlocked());
  const total = ALL_ACHIEVEMENTS.length;
  const count = unlocked.size;

  const result: OutputItem[] = [
    ...output,
    { type: 'text', content: `=== TROPHY ROOM ===  ${count} / ${total} UNLOCKED`, key: `ach-title-${t}` },
    { type: 'text', content: '', key: `ach-spacer-${t}` },
  ];

  ALL_ACHIEVEMENTS.forEach((a, i) => {
    const isUnlocked = unlocked.has(a.id);
    if (isUnlocked) {
      result.push({
        type: 'text',
        content: `  ${a.emoji}  ${a.title.toUpperCase()} — ${a.hint}`,
        key: `ach-${i}-${t}`,
      });
    } else {
      result.push({
        type: 'text',
        content: `  🔒  ???????? — ${a.hint}`,
        key: `ach-${i}-${t}`,
      });
    }
  });

  result.push({ type: 'text', content: '', key: `ach-end-spacer-${t}` });
  if (count === total) {
    result.push({ type: 'text', content: '✨ COMPLETIONIST. THE TERMINAL SALUTES YOU.', key: `ach-perfect-${t}` });
  } else {
    result.push({ type: 'text', content: 'TYPE "RESET PROGRESS" TO START OVER.', key: `ach-hint-${t}` });
  }
  return result;
};

// ===== Easter eggs =====

const FORTUNES = [
  '七転び八起き — Fall seven times, rise eight.',
  '猿も木から落ちる — Even monkeys fall from trees.',
  '塵も積もれば山となる — Dust piled high becomes a mountain.',
  '出る杭は打たれる — The nail that sticks out gets hammered down.',
  '花より団子 — Dumplings over flowers. (Substance over style.)',
  '魚心あれば水心 — If the fish has heart, the water has heart.',
  '虎穴に入らずんば虎子を得ず — If you do not enter the tiger\'s cave, you will not catch its cub.',
  '案ずるより産むが易し — Giving birth is easier than worrying about it.',
  '十人十色 — Ten people, ten colors.',
  '一期一会 — One time, one meeting. (Treasure every encounter.)',
  'Most software bugs are written by senior engineers in a hurry.',
  'The best time to plant a tree was 20 years ago. The second best is now.',
  'If you can\'t explain it simply, you don\'t understand it well enough.',
];

const KOANS = [
  'What is the sound of one hand clapping?',
  'Before enlightenment: chop wood, carry water.\nAfter enlightenment: chop wood, carry water.',
  'When you can do nothing, what can you do?',
  'If you meet the Buddha on the road, kill him.',
  'A monk asked Joshu, "Does a dog have Buddha-nature?"\nJoshu replied, "Mu."',
  'The path that can be walked is not the eternal path.',
  'Empty your cup so that it may be filled.',
];

const processFortuneCommand = (output: OutputItem[]): OutputItem[] => {
  const t = Date.now();
  const f = FORTUNES[Math.floor(Math.random() * FORTUNES.length)];
  return [
    ...output,
    { type: 'text', content: '🥠  ' + f, key: `fortune-${t}` },
  ];
};

const processKoanCommand = (output: OutputItem[]): OutputItem[] => {
  const t = Date.now();
  const k = KOANS[Math.floor(Math.random() * KOANS.length)];
  return [
    ...output,
    { type: 'text', content: '☯  ' + k, key: `koan-${t}` },
    { type: 'text', content: '   ...', key: `koan-end-${t}` },
  ];
};

const processMoonCommand = (output: OutputItem[]): OutputItem[] => {
  const t = Date.now();
  // Conway's algorithm — approximate moon phase (0 to 7, where 0/8 = new, 4 = full)
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth() + 1;
  const d = now.getUTCDate();
  let r = y % 100;
  r = r % 19;
  if (r > 9) r = r - 19;
  r = (r * 11) % 30 + m + d;
  if (m < 3) r += 2;
  r -= (y < 2000) ? 4 : 8.3;
  r = Math.floor(r + 0.5) % 30;
  const phaseValue = (r < 0) ? r + 30 : r;
  const phaseIdx = Math.floor((phaseValue / 30) * 8) % 8;
  const phases = [
    { glyph: '🌑', name: 'NEW MOON',         jp: '新月' },
    { glyph: '🌒', name: 'WAXING CRESCENT',  jp: '三日月' },
    { glyph: '🌓', name: 'FIRST QUARTER',    jp: '上弦の月' },
    { glyph: '🌔', name: 'WAXING GIBBOUS',   jp: '十三夜月' },
    { glyph: '🌕', name: 'FULL MOON',        jp: '満月' },
    { glyph: '🌖', name: 'WANING GIBBOUS',   jp: '十六夜' },
    { glyph: '🌗', name: 'LAST QUARTER',     jp: '下弦の月' },
    { glyph: '🌘', name: 'WANING CRESCENT',  jp: '有明月' },
  ];
  const p = phases[phaseIdx];
  return [
    ...output,
    { type: 'text', content: `${p.glyph}  ${p.name} (${p.jp})`, key: `moon-${t}` },
    { type: 'text', content: `   Lunar age: ~${phaseValue.toFixed(1)} days into the cycle.`, key: `moon-age-${t}` },
  ];
};

const processTeaCommand = (output: OutputItem[]): OutputItem[] => {
  const t = Date.now();
  return [
    ...output,
    { type: 'text', content: '    (  (', key: `tea-1-${t}` },
    { type: 'text', content: '     ) )', key: `tea-2-${t}` },
    { type: 'text', content: '  ........', key: `tea-3-${t}` },
    { type: 'text', content: '  |      |]', key: `tea-4-${t}` },
    { type: 'text', content: '  \\      /', key: `tea-5-${t}` },
    { type: 'text', content: '   `----\'', key: `tea-6-${t}` },
    { type: 'text', content: '', key: `tea-7-${t}` },
    { type: 'text', content: 'お茶をどうぞ — A cup of tea is ready. Steep for 3 minutes.', key: `tea-8-${t}` },
  ];
};

const processSudoCommand = (output: OutputItem[], command: string): OutputItem[] => {
  const t = Date.now();
  SoundManager.playSound('error');
  const user = 'visitor';
  const lines = [
    `[sudo] password for ${user}: `,
    `Sorry, user ${user} is not in the sudoers file. This incident will be reported.`,
  ];
  if (command === 'SUDO RM -RF /' || command === 'SUDO RM -RF / --NO-PRESERVE-ROOT') {
    lines.push('');
    lines.push('Nice try.');
  }
  return [
    ...output,
    ...lines.map((line, i) => ({ type: 'text' as const, content: line, key: `sudo-${i}-${t}` })),
  ];
};

const processWhereamiCommand = (output: OutputItem[]): OutputItem[] => {
  const t = Date.now();
  const geo = getCachedGeo();
  if (!geo) {
    return [
      ...output,
      { type: 'text', content: '... still resolving your location. Try again in a moment.', key: `where-pending-${t}` },
    ];
  }
  const greeting = buildGreeting(geo);
  const lines: { type: 'text'; content: string; key: string }[] = [
    { type: 'text', content: '=== TRIANGULATING ===', key: `where-1-${t}` },
  ];
  if (greeting) {
    lines.push({ type: 'text', content: `${greeting.emoji ?? ''}  ${greeting.primary}`, key: `where-greet-${t}` });
    if (greeting.subtitle) lines.push({ type: 'text', content: `   ${greeting.subtitle}`, key: `where-sub-${t}` });
    lines.push({ type: 'text', content: '', key: `where-pad1-${t}` });
  }
  if (geo.city)        lines.push({ type: 'text', content: `City:       ${geo.city}`, key: `where-city-${t}` });
  if (geo.region)      lines.push({ type: 'text', content: `Region:     ${geo.region}`, key: `where-reg-${t}` });
  if (geo.country)     lines.push({ type: 'text', content: `Country:    ${geo.country} (${geo.country_code ?? '??'})`, key: `where-c-${t}` });
  if (geo.timezone)    lines.push({ type: 'text', content: `Timezone:   ${geo.timezone}`, key: `where-tz-${t}` });
  if (geo.latitude !== undefined && geo.longitude !== undefined) {
    lines.push({ type: 'text', content: `Coords:     ${geo.latitude?.toFixed(3)}, ${geo.longitude?.toFixed(3)}`, key: `where-co-${t}` });
  }
  if (geo.ip) lines.push({ type: 'text', content: `IP:         ${geo.ip}`, key: `where-ip-${t}` });
  return [...output, ...lines];
};

const processWeatherCommand = (output: OutputItem[], cityKey: CityKey | null): OutputItem[] => {
  const t = Date.now();

  // No argument: show all 4 cities in a compact summary
  if (!cityKey) {
    fetchAllCities().catch(() => {});
    const rows: { type: 'text'; content: string; key: string }[] = [
      { type: 'text', content: '=== LIVE WEATHER (4 CITIES) ===', key: `wx-title-${t}` },
      { type: 'text', content: '', key: `wx-pad-${t}` },
    ];
    let anyData = false;
    for (const k of CITY_KEYS) {
      const w = getCachedWeatherFor(k);
      const c = CITIES[k];
      if (!w) {
        rows.push({ type: 'text', content: `  ${c.flag} ${c.label.padEnd(10)} ...fetching`, key: `wx-${k}-${t}` });
      } else {
        anyData = true;
        const code = describeCode(w.weatherCode);
        rows.push({
          type: 'text',
          content: `  ${c.flag} ${c.label.padEnd(10)} ${code.emoji} ${String(Math.round(w.temperatureC)).padStart(3)}°C  ${code.label}`,
          key: `wx-${k}-${t}`,
        });
      }
    }
    rows.push({ type: 'text', content: '', key: `wx-pad2-${t}` });
    rows.push({ type: 'text', content: 'Try: WEATHER LYON | WEATHER BEIRUT | WEATHER KAMPALA | WEATHER KARAKOTO', key: `wx-hint-${t}` });
    if (!anyData) {
      rows.push({ type: 'text', content: '(data not yet loaded — try again in a moment)', key: `wx-pending-${t}` });
    }
    return [...output, ...rows];
  }

  // Specific city
  const city = CITIES[cityKey];
  const w = getCachedWeatherFor(cityKey);
  fetchWeatherFor(cityKey).catch(() => {});

  if (!w) {
    return [
      ...output,
      { type: 'text', content: `... fetching weather from ${city.label}, try again in a moment.`, key: `wx-pending-${t}` },
    ];
  }
  const code = describeCode(w.weatherCode);
  const age = Math.floor((Date.now() - w.fetchedAt) / 60000);
  return [
    ...output,
    { type: 'text', content: `=== ${city.label.toUpperCase()} LIVE WEATHER ===`, key: `wx-title-${t}` },
    { type: 'text', content: `Location:    ${city.flag} ${city.labelLocal ? city.labelLocal + ' (' + city.label + ')' : city.label}`, key: `wx-loc-${t}` },
    { type: 'text', content: `Conditions:  ${code.emoji}  ${code.label}${cityKey === 'karakoto' ? ' (' + code.jp + ')' : ''}`, key: `wx-cond-${t}` },
    { type: 'text', content: `Temperature: ${Math.round(w.temperatureC)}°C`, key: `wx-temp-${t}` },
    { type: 'text', content: `Wind:        ${Math.round(w.windKph)} km/h`, key: `wx-wind-${t}` },
    { type: 'text', content: `Time:        ${w.isDay ? 'Day' : 'Night'} in ${city.label}`, key: `wx-day-${t}` },
    { type: 'text', content: ``, key: `wx-pad-${t}` },
    { type: 'text', content: `Data age: ${age} min — via Open-Meteo`, key: `wx-meta-${t}` },
  ];
};

const processWhoisCommand = (output: OutputItem[], command: string): OutputItem[] => {
  const t = Date.now();
  const target = command === 'WHOIS' ? '' : command.substring(6).trim().toLowerCase();
  if (target === '' || target === 'gb4k' || target === 'gb4k.fr') {
    return [
      ...output,
      { type: 'text', content: 'Domain Name:       GB4K.FR', key: `w-1-${t}` },
      { type: 'text', content: 'Registry:          ENTITY-OF-INTEREST', key: `w-2-${t}` },
      { type: 'text', content: 'Operator:          G. BOURGOGNE', key: `w-3-${t}` },
      { type: 'text', content: 'Status:            ACTIVE — RESTLESS', key: `w-4-${t}` },
      { type: 'text', content: 'Nameservers:       NS1.HUMANITARIAN.NET / NS2.OCEAN.BLUE', key: `w-5-${t}` },
      { type: 'text', content: 'Org:               3CAUSES / ROTARY BEIRUT CEDARS / KOJIMA KARAKOTO LLC', key: `w-6-${t}` },
      { type: 'text', content: 'Contact:           gbourgogne4000k@gmail.com', key: `w-7-${t}` },
      { type: 'text', content: 'Notes:             "real, lasting change comes from listening deeply"', key: `w-8-${t}` },
    ];
  }
  return [
    ...output,
    { type: 'text', content: `No match for "${target}".`, key: `whois-no-${t}` },
    { type: 'text', content: 'TRY: WHOIS GB4K', key: `whois-hint-${t}` },
  ];
};

const processProgressCommand = (output: OutputItem[]): OutputItem[] => {
  const t = Date.now();
  const visited = getVisitedPages();
  const unlocked = getUnlocked();
  return [
    ...output,
    { type: 'text', content: '=== PROGRESS ===', key: `prog-title-${t}` },
    { type: 'text', content: `PAGES VISITED:  ${visited.length}`, key: `prog-1-${t}` },
    { type: 'text', content: `ACHIEVEMENTS:   ${unlocked.length} / ${ALL_ACHIEVEMENTS.length}`, key: `prog-2-${t}` },
    { type: 'text', content: '', key: `prog-3-${t}` },
    { type: 'text', content: `LAST VISITED: ${visited.slice(-5).join(', ') || '(none yet)'}`, key: `prog-4-${t}` },
  ];
};